import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X } from "lucide-react";
import { COOKIE_CONSENT_KEY, LEGAL_DOCUMENTS } from "../data/legalContent";

const LegalModalsContext = createContext(null);

function LegalBody({ doc }) {
  return (
    <div className="legal-modal-body">
      <p className="legal-modal-updated">Last updated: {doc.updated}</p>
      {doc.sections.map((section) => (
        <div key={section.heading} className="legal-modal-section">
          <h3>{section.heading}</h3>
          <p>{section.body}</p>
        </div>
      ))}
    </div>
  );
}

function LegalModal({ open, onClose, doc, children }) {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKey);
    };
  }, [open, onClose]);

  return (
    <AnimatePresence>
      {open && doc ? (
        <motion.div
          className="legal-modal-root"
          role="presentation"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
        >
          <button
            type="button"
            className="legal-modal-backdrop"
            aria-label="Close dialog"
            onClick={onClose}
          />
          <motion.div
            role="dialog"
            aria-modal="true"
            aria-labelledby="legal-modal-title"
            className="legal-modal-panel"
            initial={{ opacity: 0, y: 24, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 16, scale: 0.98 }}
            transition={{ duration: 0.25, ease: [0.21, 0.47, 0.32, 0.98] }}
          >
            <div className="legal-modal-header">
              <h2 id="legal-modal-title" className="legal-modal-title">
                {doc.title}
              </h2>
              <button
                type="button"
                className="legal-modal-close"
                onClick={onClose}
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <LegalBody doc={doc} />
            {children ? (
              <div className="legal-modal-footer">{children}</div>
            ) : (
              <div className="legal-modal-footer">
                <button
                  type="button"
                  className="legal-modal-btn legal-modal-btn-primary"
                  onClick={onClose}
                >
                  Close
                </button>
              </div>
            )}
          </motion.div>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
}

function CookieConsentBanner({ visible, onAccept, onReject, onLearnMore }) {
  return (
    <AnimatePresence>
      {visible ? (
        <motion.aside
          className="cookie-banner"
          role="dialog"
          aria-label="Cookie consent"
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 40 }}
          transition={{ duration: 0.35, ease: [0.21, 0.47, 0.32, 0.98] }}
        >
          <div className="cookie-banner-inner">
            <p className="cookie-banner-text">
              We use essential cookies to run PagePeers and optional cookies to
              understand how the site is used. See our{" "}
              <button type="button" className="cookie-banner-link" onClick={onLearnMore}>
                Cookie Policy
              </button>
              .
            </p>
            <div className="cookie-banner-actions">
              <button
                type="button"
                className="legal-modal-btn legal-modal-btn-ghost"
                onClick={onReject}
              >
                Reject cookies
              </button>
              <button
                type="button"
                className="legal-modal-btn legal-modal-btn-primary"
                onClick={onAccept}
              >
                Accept cookies
              </button>
            </div>
          </div>
        </motion.aside>
      ) : null}
    </AnimatePresence>
  );
}

export function LegalModalsProvider({ children }) {
  const [activeModal, setActiveModal] = useState(null);
  const [consent, setConsent] = useState(() => {
    try {
      return localStorage.getItem(COOKIE_CONSENT_KEY);
    } catch {
      return null;
    }
  });
  const [bannerVisible, setBannerVisible] = useState(false);

  useEffect(() => {
    try {
      setBannerVisible(!localStorage.getItem(COOKIE_CONSENT_KEY));
    } catch {
      setBannerVisible(true);
    }
  }, []);

  const openLegal = useCallback((type) => {
    if (LEGAL_DOCUMENTS[type]) setActiveModal(type);
  }, []);

  const closeLegal = useCallback(() => setActiveModal(null), []);

  const acceptCookies = useCallback(() => {
    try {
      localStorage.setItem(COOKIE_CONSENT_KEY, "accepted");
    } catch {
      /* ignore */
    }
    setConsent("accepted");
    setBannerVisible(false);
    setActiveModal(null);
  }, []);

  const rejectCookies = useCallback(() => {
    try {
      localStorage.setItem(COOKIE_CONSENT_KEY, "rejected");
    } catch {
      /* ignore */
    }
    setConsent("rejected");
    setBannerVisible(false);
    setActiveModal(null);
  }, []);

  const doc = activeModal ? LEGAL_DOCUMENTS[activeModal] : null;
  const isCookies = activeModal === "cookies";

  const value = useMemo(
    () => ({ openLegal, closeLegal, consent, acceptCookies, rejectCookies }),
    [openLegal, closeLegal, consent, acceptCookies, rejectCookies],
  );

  return (
    <LegalModalsContext.Provider value={value}>
      {children}
      <LegalModal open={Boolean(doc) && !isCookies} onClose={closeLegal} doc={doc} />
      <LegalModal open={isCookies} onClose={closeLegal} doc={doc}>
        <div className="legal-modal-actions-row">
          <button
            type="button"
            className="legal-modal-btn legal-modal-btn-ghost"
            onClick={rejectCookies}
          >
            Reject cookies
          </button>
          <button
            type="button"
            className="legal-modal-btn legal-modal-btn-primary"
            onClick={acceptCookies}
          >
            Accept cookies
          </button>
        </div>
      </LegalModal>
      <CookieConsentBanner
        visible={bannerVisible && !activeModal}
        onAccept={acceptCookies}
        onReject={rejectCookies}
        onLearnMore={() => openLegal("cookies")}
      />
    </LegalModalsContext.Provider>
  );
}

export function useLegalModals() {
  const ctx = useContext(LegalModalsContext);
  if (!ctx) {
    throw new Error("useLegalModals must be used within LegalModalsProvider");
  }
  return ctx;
}
